import React, { useState } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";

type MarketStep = {
  aPrice: number;
  bPrice: number;
};

type InitialState = {
  aSlots: number;
  aBuyPrice: number;
};

type ActionPlan = {
  step: number;
  aPrice: number;
  bPrice: number;
  suggestion: string;
  profitIfA: number;
  profitIfB: number;
  requiredInvestment: number;
};

const buildScenarioPlan = (
  initial: InitialState,
  marketSteps: MarketStep[],
  payoutPerSlot = 10,
  commissionPercent = 15
): ActionPlan[] => {
  const result: ActionPlan[] = [];
  const { aSlots, aBuyPrice } = initial;
  const totalInvestmentA = aSlots * aBuyPrice;

  for (let i = 0; i < marketSteps.length; i++) {
    const { aPrice, bPrice } = marketSteps[i];

    let suggestion = "HOLD - No action needed";
    let bestProfitIfA = 0;
    let bestProfitIfB = -totalInvestmentA;
    let requiredInvestment = 0;

    if (aPrice > aBuyPrice * 1.3) {
      const sellRevenue = aSlots * aPrice;
      const profit = sellRevenue - totalInvestmentA;
      suggestion = `SELL all ${aSlots} A slots @ ₹${aPrice} — Profit ₹${profit}`;
      requiredInvestment = totalInvestmentA;
      result.push({
        step: i + 1,
        aPrice,
        bPrice,
        suggestion,
        profitIfA: profit,
        profitIfB: profit,
        requiredInvestment,
      });
      continue;
    }

    const hedgeBSlots = Math.ceil(
      totalInvestmentA / (0.85 * (payoutPerSlot - bPrice))
    );
    const costOfHedge = hedgeBSlots * bPrice;
    const bWinReturn = hedgeBSlots * payoutPerSlot;
    const profitB =
      (bWinReturn - costOfHedge - totalInvestmentA) *
      (1 - commissionPercent / 100);
    const profitA =
      (aSlots * payoutPerSlot - totalInvestmentA) *
      (1 - commissionPercent / 100);

    if (profitB >= 0) {
      suggestion = `BUY ${hedgeBSlots} B slots @ ₹${bPrice} to hedge and stay safe`;
      bestProfitIfB = Math.floor(profitB);
      bestProfitIfA = Math.floor(profitA);
      requiredInvestment = costOfHedge;
    } else {
      suggestion = `RISKY: You’ll lose ₹${Math.abs(
        Math.floor(profitB)
      )} if B wins`;
      bestProfitIfB = Math.floor(profitB);
      bestProfitIfA = Math.floor(profitA);
      requiredInvestment = costOfHedge;
    }

    result.push({
      step: i + 1,
      aPrice,
      bPrice,
      suggestion,
      profitIfA: bestProfitIfA,
      profitIfB: bestProfitIfB,
      requiredInvestment,
    });
  }

  return result;
};

const PlannerForm = () => {
  const [aSlots, setASlots] = useState<number>(1000);
  const [aBuyPrice, setABuyPrice] = useState<number>(5);
  const [currentAPrice, setCurrentAPrice] = useState<number>(5);
  const [currentBPrice, setCurrentBPrice] = useState<number>(5);
  const [plan, setPlan] = useState<ActionPlan[]>([]);

  const marketSteps: MarketStep[] = [
    { aPrice: 0.5, bPrice: 9.5 },
    { aPrice: 1, bPrice: 9 },
    { aPrice: 1.5, bPrice: 8.5 },
    { aPrice: 2, bPrice: 8 },
    { aPrice: 2.5, bPrice: 7.5 },
    { aPrice: 3, bPrice: 7 },
    { aPrice: 3.5, bPrice: 6.5 },
    { aPrice: 4, bPrice: 6 },
    { aPrice: 4.5, bPrice: 5.5 },
    { aPrice: 5, bPrice: 5 },
    { aPrice: 5.5, bPrice: 4.5 },
    { aPrice: 6, bPrice: 4 },
    { aPrice: 6.5, bPrice: 3.5 },
    { aPrice: 7, bPrice: 3 },
    { aPrice: 8, bPrice: 2 },
    { aPrice: 9, bPrice: 1 },
    { aPrice: 9.5, bPrice: 0.5 },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const initialState: InitialState = { aSlots, aBuyPrice };
    const actionPlan = buildScenarioPlan(initialState, marketSteps);
    setPlan(actionPlan);
  };

  const originalInvestment = aSlots * aBuyPrice;
  const sellValue = aSlots * currentAPrice;
  const sellProfit = sellValue - originalInvestment;

  const payoutPerSlot = 10;
  const commissionPercent = 15;

  const hedgeBSlots = Math.ceil(
    originalInvestment / (0.85 * (payoutPerSlot - currentBPrice))
  );
  const hedgeCost = hedgeBSlots * currentBPrice;
  const bWinReturn = hedgeBSlots * payoutPerSlot;
  const hedgeProfit =
    (bWinReturn - hedgeCost - originalInvestment) *
    (1 - commissionPercent / 100);

  return (
    <div>
      <h2>Planner</h2>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Number of Slots for Team A"
          type="number"
          value={aSlots}
          onChange={(e) => setASlots(Number(e.target.value))}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Buy Price per Slot for Team A"
          type="number"
          value={aBuyPrice}
          onChange={(e) => setABuyPrice(Number(e.target.value))}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Current Price of Team A"
          type="number"
          value={currentAPrice}
          onChange={(e) => setCurrentAPrice(Number(e.target.value))}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Current Price of Team B"
          type="number"
          value={currentBPrice}
          onChange={(e) => setCurrentBPrice(Number(e.target.value))}
          fullWidth
          required
          margin="normal"
        />
        <Button type="submit" variant="contained" color="primary">
          Generate Action Plan
        </Button>
      </form>

      <div style={{ marginTop: "1rem" }}>
        <Typography variant="h6">📊 Current Profit/Loss</Typography>
        <Typography style={{ color: sellProfit >= 0 ? green[700] : red[700] }}>
          Sell A now:{" "}
          {sellProfit >= 0
            ? `Profit ₹${sellProfit}`
            : `Loss ₹${Math.abs(sellProfit)}`}
        </Typography>
        <Typography style={{ color: hedgeProfit >= 0 ? green[700] : red[700] }}>
          Hedge B now:{" "}
          {hedgeProfit >= 0
            ? `Profit ₹${Math.floor(hedgeProfit)}`
            : `Loss ₹${Math.abs(Math.floor(hedgeProfit))}`}
        </Typography>
      </div>

      <h3>📋 Action Plan</h3>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Step</TableCell>
              <TableCell>A Price</TableCell>
              <TableCell>B Price</TableCell>
              <TableCell>Suggested Action</TableCell>
              <TableCell>Profit If A Wins</TableCell>
              <TableCell>Profit If B Wins</TableCell>
              <TableCell>Investment Required</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.map((step) => (
              <TableRow
                key={step.step}
                style={
                  step.aPrice === currentAPrice && step.bPrice === currentBPrice
                    ? { backgroundColor: "#fff9c4" }
                    : {}
                }
              >
                <TableCell>{step.step}</TableCell>
                <TableCell>{step.aPrice}</TableCell>
                <TableCell>{step.bPrice}</TableCell>
                <TableCell>{step.suggestion}</TableCell>
                <TableCell
                  style={{
                    color: step.profitIfA >= 0 ? green[700] : red[700],
                  }}
                >
                  ₹{step.profitIfA}
                </TableCell>
                <TableCell
                  style={{
                    color: step.profitIfB >= 0 ? green[700] : red[700],
                  }}
                >
                  ₹{step.profitIfB}
                </TableCell>
                <TableCell>₹{step.requiredInvestment}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PlannerForm;
